// src/stores/device.js
import { ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { useMacroStore } from './macro.js'
import { useKeyboardStore } from './keyboard.js'
import { convertJsonToPackets, arrayToHexString } from '../utils/dataConverter.js'
import { keycodeObj, readTag, readU16, readU32 } from '../utils/index.js'
import { getDeviceConfig, getDeviceLinkMode, getPairedDeviceConfig } from '../config/keyboardDevices.js'
import { DeviceAdapter } from '../utils/deviceAdapter.js'
import { createLayerDumpModule } from './device/layerDump.js'
import { createQldmpModule } from './device/qldmp.js'
import { loadLanguageAsync } from '../i18n.js'
import { useKeyboardTheme } from '@/composables/useKeyboardTheme'
import { findOuterIndexByReportId } from '../utils/index.js'
const reportId = 0x04 // 固定的reportId
export const useDeviceStore = defineStore('device', () => {
    const keyboardStore = useKeyboardStore()
    const { verifyImageHash, saveImageHash, calculateCRC32, rotateBase64Left90 } = useKeyboardTheme()
    // 使用设备适配器替代直接使用 WebHID device
    const deviceAdapter = new DeviceAdapter()
    const device = ref(null)

    const productName = ref('') // 记录设备名称
    const deviceVendorId = ref(null) // 记录设备 VID
    const deviceProductId = ref(null) // 记录设备 PID
    // const isConnected = ref(false)
    const pressedKeys = ref(new Set()) // 记录当前按下的键
    const isOpened = ref(false) // 记录设备是否已打开
    const currentLinkMode = ref('wired') // 当前设备链路模式（wired / wireless）
    const reportedLinkMode = ref(null) // 设备上报的链路模式（wired / wireless）
    const needReauthorize = ref(false) // 模式切换时是否需要用户重新授权
    // 宏录制相关状态
    const isRecording = ref(false) // 是否正在录制宏
    const recordingActions = ref([]) // 当前录制的动作序列
    const isInsertMode = ref(false) // 是否处于插入模式
    const insertPosition = ref(-1) // 插入位置
    const insertDirection = ref('') // 插入方向: 'above' 或 'below'
    const timeIntervalType = ref('reality') // 'reality' 或 'default'
    const defaultTimeInterval = ref(10) // 默认时间间隔值
    const dataBuffer = ref([])
    const expectedDataSize = ref(0)
    const isReceivingData = ref(false)
    const sendReportTime = ref('')
    const transmitMode = ref('wired') // WebHID传输模式: 'wired' (有线) 或 '250byte' (无线)

    // 无线模式包丢失检测和重传机制
    const currentPackets = ref([]) // 当前传输的所有数据包（用于重传）
    const ackReceived = ref(false) // 是否收到ACK响应
    const ackTimeout = ref(null) // ACK超时计时器
    const ackWaitTime = ref(10000) // 等待ACK的超时时间（毫秒）
    const lostPackets = ref([]) // 丢失的包序号列表
    // 图片接收相关状态
    const imageReceiveBuffer = ref({})  // 用于存储接收中的图片数据
    const receivingImage = ref(null)    // 当前接收中的图片信息 {page, slot, totalSize, crc, currentPacket, totalPackets, data}
    const imageRequestQueue = ref([])   // 图片请求队列 [{page, slot}, ...]
    const isReceivingImage = ref(false) // 是否正在接收图片
    const imageAckReceived = ref(false) // 是否收到图片传输ACK
    const imageAckMissingPackets = ref([]) // 图片ACK中返回的丢包序号（1-based）
    const isWaitingImageAck = ref(false) // 当前是否处于图片ACK等待窗口
    const lightData = ref({
        lightMode: null,
        speed: null,
        keyboardType: null,
        luminanceLevel: null,
        scr: 3,  // Fn 页面索引 (0-2)，对应 UI 显示的 1-3 页
        style: 5,
        baseHs: [],
        slpt: 0
    })
    let dockConnect = ref('Disconnected')
    let times = 0
    const RGB_FRAME_MAX_LED_COUNT = 79
    const rgbFrameEnabled = ref(false)
    const rgbFrameSeq = ref(-1)
    const rgbFrameFps = ref(0)
    const rgbFrameBuffer = ref(new Uint8Array(RGB_FRAME_MAX_LED_COUNT * 3))
    let rgbHeartbeatTimer = null
    let rgbFrameCount = 0
    let rgbFpsTick = Date.now()
    const MACRO_PACKET_PAYLOAD_SIZE = 484
    const MACRO_PACKET_DATA_OFFSET = 5
    const LAYER_DUMP_CHUNK_COUNT = 4
    const LAYER_DUMP_CHUNK_SIZE = 256
    const LAYER_DUMP_TOTAL_SIZE = LAYER_DUMP_CHUNK_COUNT * LAYER_DUMP_CHUNK_SIZE
    const layerDumpChunks = ref(new Map())
    const layerDumpData = ref(new Map())
    const layerDumpWaiters = ref(new Map())
    const QUICK_LAUNCH_STORAGE_KEY = 'layerQuickLaunchDump'
    const QUICK_LAUNCH_VIEW_STORAGE_KEY = 'layerQuickLaunchViewData'
    const qldmpSessions = ref(new Map())
    const qldmpWaiters = ref(new Map())

    // 宏回传核心状态
    const state = reactive({
        isActive: false,       // 是否正在接收任务
        total: 0,              // CRTS 定义的总包数
        received: new Map(),   // 已收到的包 (Key: 序号, Value: Uint8Array)
        metadata: {}           // CRTS 解析出的配置信息
    });
    // 重置状态
    const reset = () => {
        state.isActive = false;
        state.total = 0;
        state.received.clear();
        state.metadata = {};
    };
    /**
 * 从 DataView 提取 3 字节整数
 * @param {DataView} view 
 * @param {number} offset 偏移量
 * @param {boolean} isLittleEndian 是否小端
 */
    const getUint24 = (view, offset, isLittleEndian = false) => {
        const b0 = view.getUint8(offset);
        const b1 = view.getUint8(offset + 1);
        const b2 = view.getUint8(offset + 2);

        return isLittleEndian
            ? b0 | (b1 << 8) | (b2 << 16)
            : (b0 << 16) | (b1 << 8) | b2;
    };
    // 去除末尾的0字节
    function removeTrailingZeros(uint8Array) {
        let validLength = uint8Array.length
        while (validLength > 0 && uint8Array[validLength - 1] === 0) {
            validLength--
        }
        return uint8Array.slice(0, validLength)
    }
    function updateRgbFrameFps() {
        rgbFrameCount++
        const now = Date.now()
        if (now - rgbFpsTick >= 1000) {
            rgbFrameFps.value = rgbFrameCount
            rgbFrameCount = 0
            rgbFpsTick = now
        }
    }

    const layerDumpModule = createLayerDumpModule({
        deviceAdapter,
        sendReport: (...args) => deviceAdapter.sendReport(...args),
        reportId,
        createDataHeader,
        sendReportTime,
        getNowTime,
        layerDumpChunks,
        layerDumpData,
        layerDumpWaiters,
        LAYER_DUMP_CHUNK_COUNT,
        LAYER_DUMP_CHUNK_SIZE,
        LAYER_DUMP_TOTAL_SIZE
    })

    const {
        handleLayerDumpChunk,
        sendLayerData: sendLayerDataByModule,
        requestLayerDump: requestLayerDumpByModule
    } = layerDumpModule

    const qldmpModule = createQldmpModule({
        deviceAdapter,
        reportId,
        createDataHeader,
        sendReportTime,
        getNowTime,
        qldmpSessions,
        qldmpWaiters,
        quickLaunchStorageKey: QUICK_LAUNCH_STORAGE_KEY,
        quickLaunchViewStorageKey: QUICK_LAUNCH_VIEW_STORAGE_KEY
    })

    const {
        tryHandleQldmpPacket: tryHandleQldmpPacketByModule,
        sendLayerQuickLaunch: sendLayerQuickLaunchByModule,
        fetchLayerQuickLaunchDump: fetchLayerQuickLaunchDumpByModule,
        syncAllLayerQuickLaunchToStorage: syncAllLayerQuickLaunchToStorageByModule,
        buildLayerQuickLaunchViewData: buildLayerQuickLaunchViewDataByModule,
        syncLayerQuickLaunchViewStorage: syncLayerQuickLaunchViewStorageByModule
    } = qldmpModule

    // 把 layerQuickLaunchDump 转换成页面层级对象：{ "0": [], "1": [], "2": [], "3": [] }
    function buildLayerQuickLaunchViewData(rawStorage = null) {
        return buildLayerQuickLaunchViewDataByModule(rawStorage)
    }

    // 将页面格式数据写入 localStorage
    function syncLayerQuickLaunchViewStorage() {
        return syncLayerQuickLaunchViewStorageByModule()
    }

    // 解析并聚合 QLDMP 多包数据。
    // 返回 true 表示该包已被 QLDMP 逻辑消费，调用方应直接 return。
    function tryHandleQldmpPacket(uint8Array) {
        return tryHandleQldmpPacketByModule(uint8Array)
    }

    function parseRgbFramePacket(reportIdFromEvent, rawData) {
        if (!rgbFrameEnabled.value) return false
        if (!rawData || rawData.length < 9) return false

        let off = -1

        // 新格式:
        // [可选0x04] 0x52 0x47 0x42 0x46 0x52 seqH seqL count payload...
        if (
            reportIdFromEvent === 0x04 &&
            rawData[0] === 0x52 && rawData[1] === 0x47 &&
            rawData[2] === 0x42 && rawData[3] === 0x46 &&
            rawData[4] === 0x52
        ) {
            off = 0
        } else if (
            rawData.length >= 10 &&
            rawData[0] === 0x04 &&
            rawData[1] === 0x52 && rawData[2] === 0x47 &&
            rawData[3] === 0x42 && rawData[4] === 0x46 &&
            rawData[5] === 0x52
        ) {
            off = 1
        } else {
            return false
        }

        const seq = (rawData[off + 5] << 8) | rawData[off + 6]
        const count = rawData[off + 7]
        const ledCount = Math.min(count, Math.floor(rgbFrameBuffer.value.length / 3))

        const payloadStart = off + 8
        const requiredBytes = payloadStart + ledCount * 3
        if (requiredBytes > rawData.length) return false

        const frame = rgbFrameBuffer.value
        for (let i = 0; i < ledCount; i++) {
            const src = payloadStart + i * 3
            const dst = i * 3
            frame[dst] = rawData[src]
            frame[dst + 1] = rawData[src + 1]
            frame[dst + 2] = rawData[src + 2]
        }

        rgbFrameSeq.value = seq
        // console.log('rgbFrameSeq', rgbFrameSeq.value)
        updateRgbFrameFps()
        return true
    }


    async function sendRgbHeartbeat() {
        if (!deviceAdapter.isOpened) return
        // if (deviceAdapter.getMode() !== 'webhid') return

        const buf = new Uint8Array(1024)
        buf[0] = 0x43 // C
        buf[1] = 0x52 // R
        buf[2] = 0x54 // T
        buf[3] = 0x00
        buf[4] = 0x00
        buf[5] = 0x52 // R
        buf[6] = 0x47 // G
        buf[7] = 0x42 // B
        buf[8] = 0x53 // S
        console.log('sendRgbHeartbeat')
        await deviceAdapter.sendReport(reportId, buf)
    }

    async function startRgbFrameSync() {
        // if (deviceAdapter.getMode() !== 'webhid') {
        //     console.warn('[RGB帧同步] 仅 WebHID 模式支持')
        //     return false
        // }
        if (!deviceAdapter.isOpened) {
            throw new Error('[RGB帧同步] 设备未连接')
        }

        rgbFrameEnabled.value = true
        rgbFrameSeq.value = -1
        rgbFrameFps.value = 0
        rgbFrameCount = 0
        rgbFpsTick = Date.now()

        if (rgbHeartbeatTimer) {
            clearInterval(rgbHeartbeatTimer)
            rgbHeartbeatTimer = null
        }

        await sendRgbHeartbeat()
        rgbHeartbeatTimer = setInterval(() => {
            sendRgbHeartbeat().catch((error) => {
                console.warn('[RGB帧同步] 心跳发送失败:', error)
                stopRgbFrameSync()
            })
        }, 2000)

        return true
    }

    function stopRgbFrameSync() {
        if (rgbHeartbeatTimer) {
            clearInterval(rgbHeartbeatTimer)
            rgbHeartbeatTimer = null
        }
        rgbFrameEnabled.value = false
        rgbFrameFps.value = 0
        rgbFrameCount = 0
    }
    // 设置默认时间间隔
    function setDefaultTimeInterval(interval) {
        // defaultTimeInterval.value = Number(interval) || 10
        defaultTimeInterval.value = Number(interval) || 10
    }

    // 设置时间间隔类型
    function setTimeIntervalType(type) {
        if (type === 'reality' || type === 'default') {
            timeIntervalType.value = type
        }
    }

    // 设置插入模式
    function setInsertMode(enabled, position = -1, direction = '') {
        isInsertMode.value = enabled
        insertPosition.value = position
        insertDirection.value = direction
        console.log('设置插入模式:', { enabled, position, direction })
    }
    // 获取宏 store 实例
    const macroStore = useMacroStore()

    const parseKeyReplaceData = (uint8Array) => {
        // 检查前缀
        if (uint8Array.length < 3 ||
            uint8Array[0] !== 114 || uint8Array[1] !==
            99 || uint8Array[2] !== 58) {
            return []
        }

        // 直接处理 Uint8Array
        const result = []
        for (let i = 3; i < uint8Array.length;
            i++) {
            if (uint8Array[i] !== 0) {
                result.push({
                    oldKey: i - 3,  // 减去前缀长度得到真实的oldKey
                    newKey: uint8Array[i]
                })
            }
        }

        return result
    }


    /**
     * 获取当前时间字符串，格式：16:00:42
     */
    function getNowTime() {
        return new Date().toLocaleTimeString('en-GB'); // 24 小时制，自动补零
    }

    async function setDevice(authorizedDevice = null) {
        try {
            console.log('[device.js] 开始连接设备，模式:', deviceAdapter.getMode())

            const mode = deviceAdapter.getMode()

            if (mode === 'webhid') {
                if (authorizedDevice) {
                    // WebHID 模式：使用已授权的设备（来自 App.vue 的自动连接）
                    console.log('[device.js] WebHID 模式 - 使用已授权设备')
                    // 直接设置到 WebHIDDevice 实例
                    deviceAdapter.device.device = authorizedDevice
                    deviceAdapter.device.productName = authorizedDevice.productName || 'Unknown Device'
                    deviceAdapter.device.vendorId = authorizedDevice.vendorId
                    deviceAdapter.device.productId = authorizedDevice.productId

                    device.value = authorizedDevice
                    productName.value = authorizedDevice.productName || 'Unknown Device'
                    deviceVendorId.value = authorizedDevice.vendorId
                    deviceProductId.value = authorizedDevice.productId
                } else {
                    // WebHID 模式：触发设备选择（来自 auth.vue 的手动连接）
                    console.log('[device.js] WebHID 模式 - 触发设备选择')
                    const connected = await deviceAdapter.connect()
                    if (!connected) {
                        console.error('[device.js] 用户未选择设备或连接失败')
                        return false
                    }
                    // 连接成功后，设备信息已在 deviceAdapter 中
                    device.value = deviceAdapter.device.device
                    productName.value = deviceAdapter.productName
                    deviceVendorId.value = deviceAdapter.vendorId
                    deviceProductId.value = deviceAdapter.productId
                }
            } else {
                // WebSocket 模式：连接到 WebSocket 服务器
                console.log('[device.js] WebSocket 模式 - 连接服务器')
                const connected = await deviceAdapter.connect()
                if (!connected) {
                    console.error('[device.js] WebSocket 连接失败')
                    return false
                }
                // 设置 device 引用为适配器（兼容性）
                device.value = deviceAdapter
                productName.value = deviceAdapter.productName
                deviceVendorId.value = deviceAdapter.vendorId
                deviceProductId.value = deviceAdapter.productId
                console.log('deviceVendorId', deviceVendorId.value, 'deviceProductId', deviceProductId.value)
            }
            // 根据设备 VID/PID 自动检测并设置键盘布局
            const deviceConfig = getDeviceConfig(deviceVendorId.value, deviceProductId.value)
            if (deviceConfig) {
                console.log('检测到设备:', deviceConfig.name, '区域:', deviceConfig.region)
                const layout = deviceConfig.region === 'eu' ? 'eu' : 'standard'
                keyboardStore.setLayout(layout)
                currentLinkMode.value = deviceConfig.linkMode || 'wired'
                needReauthorize.value = false

                // 按链路模式设置传输模式：wireless -> 250byte, wired -> wired
                if (currentLinkMode.value === 'wireless') {
                    console.log('[device.js] 检测到无线链路，自动切换到 250byte 模式')
                    setTransmitMode('250byte')
                } else {
                    console.log('[device.js] 检测到有线链路，使用 wired 模式')
                    setTransmitMode('wired')
                }
            } else {
                console.warn('未识别的设备，使用标准布局和 wired 模式')
                keyboardStore.setLayout('standard')
                currentLinkMode.value = getDeviceLinkMode(deviceVendorId.value, deviceProductId.value) || 'wired'
                setTransmitMode('wired')  // 默认使用有线模式
            }

            // ✨ CRITICAL: 必须在 open() 之前设置所有回调，以避免竞态条件
            // 设置语言切换回调
            deviceAdapter.onlanguagechange = async (language) => {
                console.log('[Device Store] 收到语言切换事件，切换到:', language)

                // 1. 异步加载并切换 UI 国际化语言
                await loadLanguageAsync(language)
                localStorage.setItem('locale', language)

                // 2. 判断是否需要同时切换键盘布局语言
                const keyboardLayoutLanguages = ['fr', 'pt', 'br', 'sp']
                if (keyboardLayoutLanguages.includes(language)) {
                    keyboardStore.setLanguage(language)
                } else {
                    keyboardStore.setLanguage('default')
                }

                console.log('[Device Store] UI 语言已切换到:', language)
            }

            // 设置设备信息变化回调（VID/PID变化时重新设置键盘布局和传输模式）
            deviceAdapter.ondevicechange = async (deviceInfo) => {
                console.log('[Device Store] 收到设备信息变化事件', deviceInfo)

                // 更新 store 中的设备信息
                deviceVendorId.value = deviceInfo.vendorId
                deviceProductId.value = deviceInfo.productId

                // 根据新的 VID/PID 自动检测并设置键盘布局和传输模式
                const newDeviceConfig = getDeviceConfig(deviceInfo.vendorId, deviceInfo.productId)
                if (newDeviceConfig) {
                    console.log('[Device Store] 检测到新设备:', newDeviceConfig.name, '区域:', newDeviceConfig.region)
                    const newLayout = newDeviceConfig.region === 'eu' ? 'eu' : 'standard'
                    keyboardStore.setLayout(newLayout)
                    console.log(`[Device Store] 键盘布局已更新为: ${newLayout}`)
                    currentLinkMode.value = newDeviceConfig.linkMode || 'wired'
                    needReauthorize.value = false

                    // 设备切换时按链路模式更新传输模式
                    if (currentLinkMode.value === 'wireless') {
                        console.log('[Device Store] 检测到新的无线链路，切换到 250byte 模式')
                        setTransmitMode('250byte')
                    } else {
                        console.log('[Device Store] 检测到新的有线链路，切换到 wired 模式')
                        setTransmitMode('wired')
                    }
                } else {
                    console.warn('[Device Store] 未识别的新设备，使用标准布局和 wired 模式')
                    keyboardStore.setLayout('standard')
                    currentLinkMode.value = getDeviceLinkMode(deviceInfo.vendorId, deviceInfo.productId) || 'wired'
                    setTransmitMode('wired')
                }
            }

            // 打开设备连接（此时所有回调已就位，不会出现竞态条件）
            await deviceAdapter.open()

            // 验证设备是否真正连接并可通信
            try {
                // 发送一个简单的获取信息命令来测试连接
                const testHeaderIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x54, 0x45, 0x53, 0x54, 0x00];
                const testHeader = createDataHeader(testHeaderIdentifier, 0);
                await deviceAdapter.sendReport(reportId, testHeader);
                console.log('设备连接验证成功')
            } catch (sendError) {
                console.warn('设备验证失败，设备可能已断开连接:', sendError)
                await deviceAdapter.close()
                device.value = null
                isOpened.value = false
                return false
            }

            isOpened.value = true
            console.log('设备已成功打开并验证连接')

            // 设置数据接收回调
            deviceAdapter.oninputreport = (event) => {
                const dataView = event.data
                const tag = readTag(dataView, 0)
                // console.log('tag', tag)
                const uint8Array = new Uint8Array(dataView.buffer)
                // 接收灯效数据
                if (parseRgbFramePacket(event.reportId, uint8Array)) {
                    return
                }
                // 接收快捷启动数据
                if (tryHandleQldmpPacket(uint8Array)) {
                    return
                }
                if (event.reportId === 4) {

                    console.log('uint8Array', new TextDecoder().decode(dataView))
                    const cleanedData = removeTrailingZeros(uint8Array)
                    if (tag === 'DEVC') {
                        if (uint8Array[9]) {
                            // 这是灯光效果
                            lightData.value.lightMode = uint8Array[7]
                            lightData.value.speed = uint8Array[8]
                            lightData.value.keyboardType = String.fromCharCode(uint8Array[9])
                            lightData.value.luminanceLevel = uint8Array[10]
                            // 兼容固件：优先尝试读取链路模式上报字节（若协议未上报则忽略）
                            setReportedLinkMode(uint8Array[6])
                            const sub = uint8Array.slice(11)
                            const end = sub.indexOf(0x00)
                            const raw = sub.slice(0, end === -1 ? sub.length : end)
                            lightData.value.firmware = new TextDecoder('ascii').decode(raw);
                        } else {
                            const copiedArray = cleanedData.slice(11);
                            const jsonData = JSON.parse(new TextDecoder().decode(copiedArray))
                            console.log('deviceJson', jsonData)
                            // 这是灯光效果
                            lightData.value.lightMode = jsonData.led_info?.mode
                            lightData.value.speed = jsonData.led_info?.speed
                            lightData.value.keyboardType = jsonData?.os[0]
                            lightData.value.luminanceLevel = jsonData.led_info?.brightness
                            lightData.value.firmware = jsonData?.version
                            lightData.value.scr = jsonData?.scr ?? 0
                            lightData.value.style = jsonData?.style || 0
                            lightData.value.flag = jsonData.led_info?.flag || 0
                            lightData.value.hsv = jsonData.led_info.hsv || []
                            lightData.value.slpt = jsonData.slpt || 0
                            // 推荐协议字段：jsonData.link_mode，可取 wired / wireless / 250byte
                            setReportedLinkMode(jsonData?.link_mode)
                            dockConnect.value = jsonData["Stream Dock"]
                            if (jsonData?.led_info?.base_hs) {
                                lightData.value.baseHs = [jsonData?.led_info?.base_hs[0], Math.ceil(jsonData?.led_info?.base_hs[1] / 255 * 100)];
                            }
                            // 同步 Fn 页面状态到 keyboard store（scr: 0-2 → currentFnPage: 1-3）
                            if (jsonData?.scr !== undefined && jsonData.scr !== null) {
                                const fnPage = Number(jsonData.scr) + 1
                                if (fnPage >= 1 && fnPage <= 7) {
                                    keyboardStore.setFnPage(fnPage)
                                    console.log(`[Device Store] 同步 Fn 页面: scr=${jsonData.scr} → page=${fnPage}`)
                                    if (fnPage === 1) {
                                        // 同步主题样式到 keyboard store
                                        if (jsonData?.style !== undefined && jsonData.style !== null) {
                                            const themeStyle = Number(jsonData.style)
                                            if (Number(jsonData.scr) === 0 && themeStyle >= 0 && themeStyle <= 5) {
                                                keyboardStore.applyThemeFromDevice(themeStyle)
                                                console.log(`[Device Store] 同步主题样式: style=${themeStyle}`)
                                            }
                                        }
                                    }
                                }
                            }
                        }

                    } else if (cleanedData[0] === 0x41 && cleanedData[1] === 0x43 && cleanedData[2] === 0x4b) {
                        // 这是无线模式的ACK命令（数据包完整性检查）
                        // 格式: 0x41, 0x43, 0x4b, 0x00, 0x00, 0xaa, 0xbb, 0xcc, 0x00, 0x00, ...
                        // 0xff 表示所有包都完整（没有丢包）
                        // 0x01 表示第1个包丢失，0x02 表示第2个包丢失，以此类推
                        console.log('[ACK] 收到设备ACK响应')
                        if (ackTimeout.value) {
                            handlePacketAck(cleanedData)
                        }
                        handleImagePacketAck(cleanedData)
                    } else if (cleanedData[0] === 0x72 && cleanedData[1] === 0x63 && cleanedData[2] === 0x3a) {
                        // 这是按键绑定的 数据
                        const jsonData = parseKeyReplaceData(cleanedData)
                        console.log('按键绑定', jsonData)
                        keyboardStore.updateKeyReplace(jsonData)
                    }
                    else if (cleanedData[0] === 0x53 && cleanedData[1] === 0x55 && cleanedData[2] === 0x43) {
                        // 这是监听 上位机是否断连
                        const jsonData = cleanedData.slice(cleanedData)
                        console.log('断连', jsonData)
                        dockConnect.value = 'Disconnected'
                    }
                    else if (tag === 'GCRC') { // 这是获取校验crc 判断
                        // console.log('crc', Array.from(cleanedData).slice(6))
                        const deviceCrcArray = Array.from(cleanedData).slice(6)
                        const convertDeviceCrcToArray = (deviceCrcArray) => {
                            const crcArray = []
                            for (let i = 0; i < deviceCrcArray.length; i += 4) {
                                const crc = [deviceCrcArray[i], deviceCrcArray[i + 1], deviceCrcArray[i + 2], deviceCrcArray[i + 3]]
                                    .map(byte => byte.toString(16).padStart(2, '0'))
                                    .join('')
                                crcArray.push(crc)
                            }
                            return crcArray
                        }

                        const crcArray = convertDeviceCrcToArray(deviceCrcArray)
                        console.log('crcArray', crcArray)
                        // 验证CRC
                        const mismatchedIndices = verifyImageHash(crcArray)
                        if (mismatchedIndices.length > 0) {
                            console.warn(`⚠️ 检测到不匹配的图片索引: ${mismatchedIndices}`)
                            // 将所有不匹配的图片加入请求队列
                            for (const index of mismatchedIndices) {
                                const page = Math.floor(index / 6) + 3
                                const slot = (index % 6)
                                imageRequestQueue.value.push({ page, slot })
                            }
                            // 开始处理队列中的第一个请求
                            processImageRequestQueue()
                        }
                    } else if (tag === 'LYDM' && uint8Array[4] === 0x50) {
                        const layer = Number(uint8Array[5])
                        const chunk = Number(uint8Array[6])
                        const payload = uint8Array.slice(8, 8 + LAYER_DUMP_CHUNK_SIZE)
                        handleLayerDumpChunk(layer, chunk, payload)
                    } else if (tag === 'GIMG' || tag === 'IMGH' || tag === 'IMGD') {
                        // 统一交给 handleImageData：
                        // IMGH/IMGD 写入 payload，GIMG 仅同步元数据
                        if (tag !== 'GIMG') {
                            handleImageData(uint8Array, tag)
                        }
                    } else if (tag === 'GEND') {
                        // 图片尾包标识 (GEND)
                        // 先同步尾包元数据（若有），再执行完成流程
                        handleImageData(cleanedData, tag)
                        handleImageEnd(cleanedData)
                    } else if (tag === 'CRTS') { // 宏录制数据信令
                        const totalBytes = getUint24(dataView, 4)
                        const totalPackets = Math.ceil(totalBytes / MACRO_PACKET_PAYLOAD_SIZE)
                        state.total = totalPackets
                        state.received.clear();
                        state.metadata.totalBytes = totalBytes
                        state.isActive = true;
                        console.log('任务启动，总字节数:', totalBytes, '总包数:', state.total);
                    }
                    else if (tag === 'RECD') {
                        console.log('data', dataView)
                        const seq = dataView.getUint8(4, false);
                        console.log('序号', seq)
                        const totalBytes = Number(state.metadata.totalBytes || 0)
                        const remaining = totalBytes > 0
                            ? Math.max(totalBytes - (seq * MACRO_PACKET_PAYLOAD_SIZE), 0)
                            : MACRO_PACKET_PAYLOAD_SIZE
                        const expectedBodyLength = Math.min(MACRO_PACKET_PAYLOAD_SIZE, remaining)
                        const availableLength = Math.max(dataView.byteLength - MACRO_PACKET_DATA_OFFSET, 0)
                        const bodyLength = Math.min(expectedBodyLength, availableLength)
                        const body = new Uint8Array(
                            dataView.buffer,
                            dataView.byteOffset + MACRO_PACKET_DATA_OFFSET,
                            bodyLength
                        );
                        console.log('body', body)
                        state.received.set(seq, body);
                    }

                    else if (tag === 'CRTE') {
                        const totalBytes = getUint24(dataView, 4)
                        const totalPackets = Math.ceil(totalBytes / MACRO_PACKET_PAYLOAD_SIZE)
                        state.total = totalPackets
                        state.metadata.totalBytes = totalBytes
                        // 1. 找出所有缺失的序号
                        const missing = [];
                        for (let i = 0; i < state.total; i++) {
                            if (!state.received.has(i)) missing.push(i);
                        }
                        // 2.发送验包信息
                        sendPocketLoss(missing);
                        // 3. 返回结果给外部处理
                        const isComplete = missing.length === 0;
                        // let finalBlob = null;

                        if (isComplete) {
                            // 组装数据
                            const mergedBytes = new Uint8Array(totalBytes)
                            let writeOffset = 0
                            for (let i = 0; i < state.total; i++) {
                                const chunk = state.received.get(i) || new Uint8Array()
                                const remain = totalBytes - writeOffset
                                if (remain <= 0) break
                                const copyLength = Math.min(chunk.length, remain)
                                mergedBytes.set(chunk.slice(0, copyLength), writeOffset)
                                writeOffset += copyLength
                            }
                            const validBytes = removeTrailingZeros(mergedBytes)
                            const macroJSON = new TextDecoder('utf-8').decode(validBytes)
                            console.log('macroJSON', macroJSON)
                            const macroData = JSON.parse(macroJSON)
                            macroStore.setExportMacro(macroData, 'equipment')
                            reset(); // 组装完清理内存
                            // setTimeout(() => {
                            //     sendKeyReplaceByDevice()
                            // }, 100)
                        }
                    } else if (tag === 'ACK') { // 图片验包信令
                        handleImagePacketAck(cleanedData)
                    }
                    else {
                        // 这是宏录制数据
                        if (cleanedData[4] === 0x73 && cleanedData[5] === 0x69) {
                            const headerJson = new TextDecoder().decode(cleanedData)
                            try {
                                const headerData = JSON.parse(headerJson)
                                expectedDataSize.value = headerData.size
                                dataBuffer.value = []
                                isReceivingData.value = true
                                console.log('开始接收数据，预期大小:', expectedDataSize.value,
                                    '事件类型:', headerData.event)
                            } catch (error) {
                                console.error('解析数据头失败:', error)
                                console.log('清理后的数据:', headerJson)
                            }
                        } else if (isReceivingData.value) {
                            dataBuffer.value.push(...cleanedData)
                            if (dataBuffer.value.length >= expectedDataSize.value - 1) {
                                const finalCleanedData = removeTrailingZeros(new Uint8Array(dataBuffer.value))
                                const macroJSON = new TextDecoder().decode(finalCleanedData)
                                const macroData = JSON.parse(macroJSON)
                                console.log('数据接收完成:', macroData)
                                macroStore.setExportMacro(macroData, 'equipment')
                                dataBuffer.value = []
                                isReceivingData.value = false
                                // setTimeout(() => {
                                //     sendKeyReplaceByDevice()
                                // }, 100)
                            }
                        }
                    }

                }
            }

            return true
        } catch (error) {
            console.error('设备打开失败:', error)
            isOpened.value = false
            device.value = null
            return false
        }
    }
    // 处理图片尾包
    function handleImageEnd(cleanedData) {
        try {
            // 优先按尾包中携带的 page/slot 精确定位；没有则回退遍历
            const endMeta = parseImagePacketMeta(cleanedData, 'GEND')
            const preferredKeys = []
            if (endMeta && Number.isInteger(endMeta.page) && Number.isInteger(endMeta.slot)) {
                const key = `fnPage${endMeta.page + 1}CustomImage_${endMeta.slot}`
                if (imageReceiveBuffer.value[key]) {
                    if (Number.isInteger(endMeta.currentPacket) && endMeta.currentPacket >= 0) {
                        imageReceiveBuffer.value[key].currentPacket = endMeta.currentPacket
                    }
                    preferredKeys.push(key)
                }
            }

            const keysToCheck = preferredKeys.length > 0 ? preferredKeys : Object.keys(imageReceiveBuffer.value)
            for (const imageKey of keysToCheck) {
                const imageInfo = imageReceiveBuffer.value[imageKey]
                if (!imageInfo) continue
                if (!Number.isInteger(imageInfo.totalPackets) || imageInfo.totalPackets <= 0) {
                    if (transmitMode.value === 'wired') {
                        const validPackets = Array.isArray(imageInfo.packets) ? imageInfo.packets.filter(Boolean) : []
                        if (validPackets.length === 0) {
                            console.warn(`[图片尾包] [${imageKey}] 有线模式下无可用数据包，跳过`)
                            continue
                        }
                        imageInfo.packets = validPackets
                        imageInfo.totalPackets = validPackets.length
                        imageInfo.receivedCount = validPackets.length
                    } else {
                        console.warn(`[图片尾包] [${imageKey}] totalPackets 无效，跳过本次完成判定`)
                        continue
                    }
                }

                // 250byte 模式：收到尾包后先验包并回传丢包信息
                if (transmitMode.value === '250byte') {
                    const missingPackets = []
                    for (let i = 0; i < imageInfo.totalPackets; i++) {
                        if (!imageInfo.packets[i]) {
                            missingPackets.push(i)
                        }
                    }

                    console.log(`[图片验包] [${imageKey}] 已收 ${imageInfo.receivedCount}/${imageInfo.totalPackets}, 丢包: ${missingPackets.join(',') || '无'}`)
                    sendImgPocketLoss(missingPackets)

                    // 无丢包才进入完成流程；有丢包则等待设备重发后下一次 GEND 再验
                    if (missingPackets.length === 0) {
                        const page = imageInfo.page
                        const slot = imageInfo.slot
                        completeImageReceive(imageKey, imageInfo, page, slot)

                        setTimeout(() => {
                            processImageRequestQueue()
                        }, 100)
                    } else {
                        console.warn(`[图片验包] 已发送丢包请求，等待设备重发 [${imageKey}]`)
                    }
                    return
                }

                // 检查该图片是否完全接收
                if (imageInfo.receivedCount === imageInfo.totalPackets) {
                    console.log(`📌 收到图片尾包，匹配到 [${imageKey}]`)

                    // 完成接收处理
                    const page = imageInfo.page
                    const slot = imageInfo.slot
                    completeImageReceive(imageKey, imageInfo, page, slot)

                    // 接收完成，处理下一个队列请求
                    setTimeout(() => {
                        processImageRequestQueue()
                    }, 100)

                    return // 找到并处理后退出
                }
            }

            console.warn(`⚠️ 收到尾包但未找到完全接收的图片`)
        } catch (error) {
            console.error('❌ 图片尾包处理失败:', error)
        }
    }

    // 处理图片请求队列
    function processImageRequestQueue() {
        // 如果队列为空，说明全部处理完成
        if (imageRequestQueue.value.length === 0) {
            isReceivingImage.value = false
            console.log('📋 图片请求队列已完成')
            return
        }

        // 如果正在接收图片，不处理下一个
        if (isReceivingImage.value) {
            return
        }

        // 取出队列中的第一个请求
        const { page, slot } = imageRequestQueue.value.shift()
        isReceivingImage.value = true

        console.log(`📤 发送图片请求 [page${page}-slot${slot}], 队列剩余: ${imageRequestQueue.value.length}`)
        sendRequestImageCommand(page, slot)
    }
    /**
 * 解析图片包元数据（支持 GIMG / IMGH / IMGD / GEND）
 * @param {Uint8Array} rawData
 * @param {'GIMG'|'IMGH'|'IMGD'|'GEND'} tag
 */
    function parseImagePacketMeta(rawData, tag) {
        if (!rawData || rawData.length < 6) return null
        const view = new DataView(rawData.buffer, rawData.byteOffset, rawData.byteLength)
        const safeReadU16 = (offset) => (rawData.length >= offset + 2 ? readU16(view, offset) : null)
        const safeReadU32 = (offset) => (rawData.length >= offset + 4 ? readU32(view, offset) : null)

        const meta = {
            page: null,
            slot: null,
            crc: null,
            totalSize: null,
            currentPacket: null,
            totalPackets: null,
            chunkLen: null
        }

        if (tag === 'IMGH') {
            if (rawData.length >= 10) {
                meta.crc = [rawData[6], rawData[7], rawData[8], rawData[9]]
            }
            meta.page = rawData[4]
            meta.slot = rawData[5]
            meta.totalSize = safeReadU32(10)
            meta.currentPacket = safeReadU16(14)
            meta.totalPackets = safeReadU16(16)
            meta.chunkLen = safeReadU16(18)
        } else if (tag === 'IMGD') {
            meta.page = rawData[4]
            meta.slot = rawData[5]
            meta.currentPacket = safeReadU16(6)
            meta.totalPackets = safeReadU16(8)
            meta.chunkLen = safeReadU16(10)
        } else if (tag === 'GEND') {
            // 尾包字段在不同链路下不稳定：有线模式可能仅补零
            if (rawData.length >= 8) {
                meta.page = rawData[6]
                meta.slot = rawData[7]
            }
            if (rawData.length >= 10) {
                meta.totalPackets = safeReadU16(8)
            }
        }

        return meta
    }
    // 处理接收的图片数据
    function handleImageData(cleanedData, tag) {
        /**
         * 图片数据协议格式:
         * [0x49, 0x4d, 0x47]           // "IMG" 标识符 (3字节)
         * 0x48                         // 固定值 (1字节)
         * [page, slot]                 // 页码和按键槽位 (2字节)
         * [crc1, crc2, crc3, crc4]     // CRC校验码 (4字节)
         * [size1, size2, size3, size4] // 图片总大小 (4字节，大端)
         * [pkgNo1, pkgNo2]             // 当前数据包编号 (2字节，大端)
         * [totalPkg1, totalPkg2]       // 总数据包数 (2字节，大端)
         * [0x01, 0xeb]                 // 忽略 (2字节)
         * [...imageData]               // 实际图片数据
         */

        try {
            const meta = parseImagePacketMeta(cleanedData, tag)
            if (!meta) return
            let { page, slot, crc, totalSize, currentPacket, chunkLen, totalPackets } = meta

            // GEND 在有线模式下可能不携带 page/slot，优先回退到当前接收上下文
            if (tag === 'GEND' && (!Number.isInteger(page) || !Number.isInteger(slot))) {
                if (receivingImage.value && Number.isInteger(receivingImage.value.page) && Number.isInteger(receivingImage.value.slot)) {
                    page = receivingImage.value.page
                    slot = receivingImage.value.slot
                } else {
                    // 无上下文时不创建无效 key，交由 handleImageEnd() 兜底遍历
                    return
                }
            }

            // 非 GEND 包必须携带有效 page/slot
            if (!Number.isInteger(page) || !Number.isInteger(slot)) {
                return
            }

            const imageKey = `fnPage${page + 1}CustomImage_${slot}`

            // 初始化或更新接收中的图片对象
            if (!imageReceiveBuffer.value[imageKey]) {
                const initTotalPackets = Number.isInteger(totalPackets) && totalPackets > 0 ? totalPackets : 0
                imageReceiveBuffer.value[imageKey] = {
                    page,
                    slot,
                    crc: Array.isArray(crc) ? crc.map(b => b.toString(16).padStart(2, '0')).join('') : '',
                    totalSize: Number.isInteger(totalSize) ? totalSize : 0,
                    totalPackets: initTotalPackets,
                    currentPacket: Number.isInteger(currentPacket) ? currentPacket : -1,
                    packets: new Array(initTotalPackets),
                    receivedCount: 0,
                }
                console.log(`🖼️ 开始接收图片 [page${page}-slot${slot}], 总大小: ${totalSize ?? '-'}字节, 总包数: ${totalPackets ?? '-'}`)
            }

            const imageInfo = imageReceiveBuffer.value[imageKey]
            if (!imageInfo) return

            // 元数据同步：头包/尾包到了也会更新 page/slot/currentPacket
            imageInfo.page = page
            imageInfo.slot = slot
            if (Array.isArray(crc) && crc.length === 4) {
                imageInfo.crc = crc.map(b => b.toString(16).padStart(2, '0')).join('')
            }
            if (Number.isInteger(totalSize) && totalSize >= 0) {
                imageInfo.totalSize = totalSize
            }
            if (Number.isInteger(totalPackets) && totalPackets > 0) {
                if (!Array.isArray(imageInfo.packets) || imageInfo.packets.length !== totalPackets) {
                    const oldPackets = Array.isArray(imageInfo.packets) ? imageInfo.packets : []
                    const resized = new Array(totalPackets)
                    for (let i = 0; i < Math.min(oldPackets.length, resized.length); i++) {
                        resized[i] = oldPackets[i]
                    }
                    imageInfo.packets = resized
                    imageInfo.receivedCount = resized.filter(Boolean).length
                }
                imageInfo.totalPackets = totalPackets
            }
            if (Number.isInteger(currentPacket) && currentPacket >= 0) {
                imageInfo.currentPacket = currentPacket
            }

            receivingImage.value = {
                page,
                slot,
                currentPacket: Number.isInteger(currentPacket) ? currentPacket : imageInfo.currentPacket ?? -1,
                totalPackets: imageInfo.totalPackets ?? null,
                totalSize: imageInfo.totalSize ?? null
            }

            // GIMG / GEND 默认只同步元数据，不写 payload
            if (!(tag === 'IMGH' || tag === 'IMGD')) {
                return
            }

            if (!Number.isInteger(currentPacket) || currentPacket < 0) {
                console.warn(`⚠️ ${tag} 包缺少有效 currentPacket，已跳过数据写入`)
                return
            }

            if (Number.isInteger(imageInfo.totalPackets) && imageInfo.totalPackets > 0 && currentPacket >= imageInfo.totalPackets) {
                console.warn(`⚠️ 包号越界 [${imageKey}] 包号=${currentPacket}, 总包数=${imageInfo.totalPackets}`)
                return
            }

            // 提取精确的图片数据（只取chunkLen字节，忽略后续填充/零字节）
            const safeChunkLen = Number.isInteger(chunkLen) && chunkLen > 0
                ? Math.min(chunkLen, Math.max(cleanedData.length - 20, 0))
                : 0
            if (safeChunkLen <= 0) {
                console.warn(`⚠️ ${tag} 包无有效 payload [${imageKey}]`)
                return
            }
            const imageData = cleanedData.slice(20, 20 + safeChunkLen)

            // 检查该数据包是否已接收（防止重复数据包覆盖计数）
            if (!imageInfo.packets[currentPacket]) {
                imageInfo.packets[currentPacket] = imageData
                imageInfo.receivedCount++
            } else {
                console.warn(`⚠️ 重复数据包 [${imageKey}] 包号: ${currentPacket}，已忽略`)
            }

            // 显示接收进度
            const denominator = imageInfo.totalPackets > 0 ? imageInfo.totalPackets : 1
            const progress = Math.round((imageInfo.receivedCount / denominator) * 100)
            console.log(`📦 接收进度 [page${page}-slot${slot}]: ${imageInfo.receivedCount}/${imageInfo.totalPackets || '-'} (${progress}%)`)

            // 注意：不在这里判断接收完成，由 handleImageEnd() 处理
        } catch (error) {
            console.error('❌ 图片数据解析失败:', error)
        }
    }

    // 完成图片接收
    async function completeImageReceive(imageKey, imageInfo, page, slot) {
        try {
            // 合并所有图片数据包
            const totalLength = imageInfo.packets.reduce((sum, packet) => sum + packet.length, 0)
            const completeImageData = new Uint8Array(totalLength)
            let offset = 0

            for (const packet of imageInfo.packets) {
                completeImageData.set(packet, offset)
                offset += packet.length
            }

            // 计算接收到的数据的CRC32
            const receivedCrc = calculateCRC32(completeImageData)
            const crcMatch = receivedCrc === imageInfo.crc
            console.log('receivedCrc', receivedCrc)
            console.log(`✅ 图片接收完成 [${imageKey}]`)
            console.log(`   总大小: ${completeImageData.length}字节`)
            console.log(`   CRC校验: ${imageInfo.crc} (${crcMatch ? '✓ 校验通过' : '✗ 校验失败'})`)

            // 保存图片数据到 localStorage
            const imageBase64 = arrayToBase64(completeImageData)
            const newBase64 = await rotateBase64Left90(imageBase64)
            // console.log(imageBase64, 'imageBase64')
            const storageKey = `${imageKey}`
            localStorage.setItem(storageKey, newBase64)
            // localStorage.setItem(`${storageKey}_crc`, imageInfo.crc)
            saveImageHash(page, slot, imageInfo.crc)
            // 更新按键图片信息
            keyboardStore.updateCustomImg(page, slot, newBase64)
            console.log(`💾 图片已保存到本地存储: ${storageKey}`)

            // 清理接收缓冲区
            delete imageReceiveBuffer.value[imageKey]

            // 重置接收状态标志，允许队列继续处理下一张图片
            isReceivingImage.value = false
        } catch (error) {
            console.error('❌ 图片接收完成处理失败:', error)
            // 即使出错也要重置状态，防止队列被永久阻塞
            isReceivingImage.value = false
        }
    }

    // // CRC32计算函数
    // function calculateCRC32(data) {
    //     // 简化的CRC32计算（实际应该使用设备协议中指定的算法）
    //     // 这里返回十六进制格式的CRC字符串
    //     let crc = 0xFFFFFFFF

    //     for (let i = 0; i < data.length; i++) {
    //         crc ^= data[i]
    //         for (let j = 0; j < 8; j++) {
    //             crc = (crc >>> 1) ^ ((crc & 1) ? 0xEDB88320 : 0)
    //         }
    //     }

    //     crc = crc ^ 0xFFFFFFFF
    //     return crc.toString(16).padStart(8, '0').toUpperCase()
    // }

    // 将 Uint8Array 转换为 Base64 字符串（带 data URL 前缀）
    function arrayToBase64(uint8Array) {
        let binary = ''
        for (let i = 0; i < uint8Array.length; i++) {
            binary += String.fromCharCode(uint8Array[i])
        }
        const base64 = btoa(binary)
        return `data:image/jpeg;base64,${base64}`
    }

    // 从 localStorage 获取图片
    function getImageFromStorage(page, slot) {
        const imageKey = `fnPage${page}CustomImage_${slot}`
        const imageDataUrl = localStorage.getItem(imageKey)

        if (!imageDataUrl) {
            console.warn(`⚠️ 未找到图片数据: ${imageKey}`)
            return null
        }

        // 处理两种格式：带 data URL 前缀 或 纯 Base64
        let base64String = imageDataUrl
        if (imageDataUrl.startsWith('data:image')) {
            // 提取 data URL 中的 base64 部分
            base64String = imageDataUrl.split(',')[1]
        }

        // 将 Base64 转换回 Uint8Array
        const binary = atob(base64String)
        const uint8Array = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
            uint8Array[i] = binary.charCodeAt(i)
        }

        return uint8Array
    }

    // 直接获取图片 data URL（用于 <img> src）
    function getImageDataUrl(page, slot) {
        const imageKey = `fnPage${page}CustomImage_${slot}`
        const imageDataUrl = localStorage.getItem(imageKey)

        if (!imageDataUrl) {
            console.warn(`⚠️ 未找到图片数据: ${imageKey}`)
            return null
        }

        // 如果已经是 data URL 格式，直接返回
        if (imageDataUrl.startsWith('data:image')) {
            return imageDataUrl
        }

        // 否则添加前缀
        return `data:image/jpeg;base64,${imageDataUrl}`
    }

    // 获取数据的字节大小
    function getDataByteSize(data) {
        if (data instanceof Uint8Array) {
            return data.length;
        }
        const jsonString = JSON.stringify(data);
        return new TextEncoder().encode(jsonString).length;
    }

    // 创建数据头
    // 支持两种模式：
    // - 'wired': 1024字节（1023数据 + 1报告ID）
    // - '250byte': 250字节（249数据 + 1报告ID）
    function createDataHeader(headerIdentifier = [], dataSize) {
        let headerSize = transmitMode.value === '250byte' ? 249 : 1023;
        const headerBuffer = new Uint8Array(headerSize);

        // 写入头部标识符
        headerIdentifier.forEach((value, index) => {
            if (index < headerBuffer.length) {
                headerBuffer[index] = value;
            }
        });

        // 写入数据大小（2字节，高字节在前）
        const sizePos = headerIdentifier.length;
        if (sizePos + 1 < headerBuffer.length) {
            headerBuffer[sizePos] = (dataSize >> 8) & 0xFF;  // 高字节
            headerBuffer[sizePos + 1] = dataSize & 0xFF;     // 低字节
        }

        // 剩余的字节保持为0
        return headerBuffer;
    }
    // 发送图片已发送结束命令
    function sendImgOver() {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x50, 0x49, 0x58, 0x4d, 0x41, 0x50, 0x45, 0x4e, 0x44, 0x49];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送图片已发送结束命令')
        return deviceAdapter.sendReport(reportId, header);
    }
    // 发送图片丢包命令
    function sendImgPocketLoss(missing = []) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        let params = []
        if (missing.length) {
            params = missing
        } else {
            params = []
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x49, 0x4d, 0x41, 0x43, 0x4B, 0x00, 0x00, ...params, 0xff];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送图片丢包命令')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送丢包命令
    function sendPocketLoss(missing = []) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        let params = []
        if (missing.length) {
            params = missing
        } else {
            params = []
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x41, 0x43, 0x4B, 0x00, 0x00, ...params, 0xff];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送丢包命令')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送获取图片的命令
    function sendRequestImageCommand(page, slot) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x47, 0x49, 0x4d, 0x47, 0x00, page, slot];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送获取图片的命令')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送获取图片校验值
    function sendGetCrc() {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x47, 0x43, 0x52, 0x43]
        const header = createDataHeader(headerIdentifier, 0)
        console.log(header, '发送获取图片校验值')
        deviceAdapter.sendReport(reportId, header)
    }
    // 发送按键绑定
    function sendKeyBinding(oldKey, newKey) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        // 创建JSON数据
        const jsonData = { oldKey, newKey }
        const jsonSize = getDataByteSize(jsonData)

        // 使用复用函数创建1024字节的数据包
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45,
            0x50, 0x4c, 0x41]
        const packet = createDataHeader(headerIdentifier, jsonSize)

        // 将JSON数据填入包中（从第14位开始）
        const jsonBytes = new
            TextEncoder().encode(JSON.stringify(jsonData))
        jsonBytes.forEach((byte, index) => {
            packet[14 + index] = byte
        })

        console.log('发送按键绑定:', jsonData)
        return deviceAdapter.sendReport(reportId, packet)
    }
    // 发送颜色
    function sendColor(switchValue, r, g, b, h, s, v) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        // console.log('sendColor', params)
        const high = (h >> 8) & 0xFF;
        const low = h & 0xFF;
        const mapS = Math.floor(s / 100 * 255) // 这里转换用math.floor 转换回来用math.ceil
        const mapV = Math.floor(v / 100 * 255)
        // const { r, g, b } = params
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x43, 0x4f, 0x4c, 0x4f, 0x52, switchValue, r, g, b, high, low, mapS, mapV];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送颜色')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送底色
    function sendHs(h, s) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const high = (h >> 8) & 0xFF;
        const low = h & 0xFF;
        const mapS = Math.floor(s / 100 * 255) // 这里转换用math.floor 转换回来用math.ceil
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x42, 0x41, 0x53, 0x45, 0x43, 0x4f, high, low, mapS]
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送hs')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送当前系统类型
    function sendOs(type) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        let osType = 0
        if (type === 'Windows') {
            osType = 0x57
        } else if (type === 'macOS') {
            osType = 0x4d
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x43, 0x50, 0x4f, 0x53, 0x00, osType];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送当前系统类型')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送获取灯效数据命令
    async function sendGetLightData() {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x49, 0x4e, 0x46, 0x4f, 0x00];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送获取灯效数据命令')
        await deviceAdapter.sendReport(reportId, header);
    }
    // 发送修改速度命令
    function sendSpeed(params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        let level = `0x0${params}`
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x53, 0x50, 0x44, 0x00, level];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送修改速度命令')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送修改亮度命令
    function sendLuminance(params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        // let level = `0x0${params}`
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x4c, 0x55, 0x4d, 0x00, params];
        const header = createDataHeader(headerIdentifier, 0);
        console.log(header, '发送修改亮度命令')
        deviceAdapter.sendReport(reportId, header);
    }
    // 发送获取按键绑定数据
    async function sendKeyReplaceByDevice(lightEffect = 0) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x49, 0x4e, 0x46, 0x01];
        const header = createDataHeader(headerIdentifier, lightEffect);
        console.log(header, '发送获取按键绑定数据')
        await deviceAdapter.sendReport(reportId, header);
    }
    // 发送获取宏数据命令
    async function sendGetMacroByDevice(lightEffect = 0) {
        return new Promise(async (resolve, reject) => {
            if (!deviceAdapter.isOpened) {
                throw new Error('设备未连接')
            }
            const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x49, 0x4e, 0x46, 0x14];
            const header = createDataHeader(headerIdentifier, lightEffect);
            console.log(header, '获取宏数据命令')
            await deviceAdapter.sendReport(reportId, header);
            resolve()
        })
    }
    // 发送重置命令
    function sendResetEffect(lightEffect = 0) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x52, 0x45, 0x43];
        const header = createDataHeader(headerIdentifier, lightEffect);
        deviceAdapter.sendReport(reportId, header);
        console.log('发送数据头成功', header);
        sendReportTime.value = getNowTime()
    }
    // 发送灯效
    function sendLightEffect(lightEffect = 0) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x4d, 0x4f, 0x44];
        const header = createDataHeader(headerIdentifier, lightEffect);
        deviceAdapter.sendReport(reportId, header);
        console.log('发送数据头成功', header);
        sendReportTime.value = getNowTime()
    }

    // 发送数据到键盘
    async function sendReport() {
        const rawData = macroStore.getCurrentMacro();
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        try {
            // 只提取需要的数据
            const data = {
                macroId: rawData.id,
                bindKeys: rawData.bindKeys.filter((item) => item !== null),
                actions: rawData.actions.map(action => ({
                    key: action.key,
                    keyCode: action.keyCode,
                    timeInterval: action.timeInterval,
                    isKeyDown: action.isKeyDown
                }))
            };
            console.log('发送数据到键盘:', data)
            const originalSize = getDataByteSize(data);
            // console.log('原始数据大小:', originalSize, '字节');
            // 检测设备模式
            const mode = deviceAdapter.getMode()
            if (mode === 'websocket11') { // 宏录制 上位机端现在 不走这里也走下面
                // WebSocket 模式：直接发送完整 JSON，不分包
                const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x43, 0x00, 0x00];
                const header = createDataHeader(headerIdentifier, originalSize);
                console.log('originalSize', originalSize)
                console.log('header', header.length)
                await deviceAdapter.sendReport(reportId, header);

                // 直接发送完整的 JSON 数据（已在 websocketDevice.js 中转 base64）
                const jsonString = JSON.stringify(data)
                const jsonBytes = new TextEncoder().encode(jsonString)
                await deviceAdapter.sendReport(reportId, jsonBytes)

                console.log('[WebSocket] 完整数据发送完成')
                sendReportTime.value = getNowTime()
            } else {
                // WebHID 模式：根据 transmitMode 选择有线或无线传输
                // 转换数据（根据传输模式选择分包大小）
                const result = convertJsonToPackets(data, transmitMode.value)
                // 首先发送数据头
                // 固定的头部标识符
                const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x43, 0x00, 0x00];
                const header = createDataHeader(headerIdentifier, originalSize, transmitMode.value);
                await deviceAdapter.sendReport(reportId, header);
                // console.log('发送数据头成功', header);

                // 然后发送实际数据
                if (result.success) {
                    // 在无线模式下，保存所有数据包用于可能的重传
                    if (transmitMode.value === '250byte') {
                        currentPackets.value = result.packets
                        console.log('[无线模式] 保存数据包用于可能的重传')
                    }

                    // 使用 for...of 循环顺序发送数据包
                    for (let i = 0; i < result.packets.length; i++) {
                        let packet = result.packets[i];
                        console.log('数据:', packet)

                        // 如果是无线模式（250byte），为每个数据包添加包序号
                        if (transmitMode.value === '250byte') {
                            const newPacket = new Uint8Array(packet.length + 1);
                            newPacket[0] = i + 1; // 包序号从1开始
                            newPacket.set(packet, 1); // 原始数据从第二个字节开始
                            packet = newPacket;
                            console.log(`无线模式：第${i + 1}包，包含包序号`)
                        }

                        // 添加延时函数
                        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

                        try {
                            // 发送数据包
                            await deviceAdapter.sendReport(reportId, packet)
                            // console.log(`数据包 ${i + 1} 发送成功`)

                            // 每次发送后等待一小段时间
                            await delay(50) // 等待50ms，可以根据实际情况调整这个值
                        } catch (error) {
                            // console.error(`数据包 ${i + 1} 发送失败:`, error)
                            throw error
                        }
                    }
                    console.log('所有数据包发送完成')

                    // 无线模式下，发送结束命令并设置ACK超时
                    if (transmitMode.value === '250byte') {
                        console.log('[无线模式] 发送结束命令...')
                        const endCommand = new Uint8Array([
                            0x43, 0x52, 0x54, 0x00, 0x00, 0x50, 0x49, 0x58,
                            0x4d, 0x41, 0x50, 0x45, 0x4e, 0x44, 0x52
                        ]);
                        try {
                            await deviceAdapter.sendReport(reportId, endCommand)
                            console.log('[无线模式] 结束命令发送成功，等待设备ACK响应...')

                            // 设置ACK超时计时器，等待设备检查包的完整性
                            ackReceived.value = false
                            setupAckTimeout()
                        } catch (error) {
                            console.error('[无线模式] 结束命令发送失败:', error)
                            throw error
                        }
                    }

                    sendReportTime.value = getNowTime()
                }
                return true
            }

        } catch (error) {
            console.error('发送数据失败:', error)
            throw error
        }
    }
    // 发送快捷启动
    function sendQuickLaunch(params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const payload = Array.isArray(params) ? params : [params]
        let fallbackLayer = 0
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x51, 0x4c, 0x53, 0x45, 0x54, ...payload];
        const header = createDataHeader(headerIdentifier, 0);
        Promise.resolve(deviceAdapter.sendReport(reportId, header)).catch((error) => {
            console.warn('[QLSET] 发送失败，尝试回读设备快捷启动数据:', error)
            fetchLayerQuickLaunchDump(fallbackLayer).catch((syncError) => {
                console.warn('[QLSET] 发送失败后回读快捷启动数据失败:', syncError)
            })
        })
        console.log('发送快捷启动', header);

        // 乐观更新本地快捷启动缓存，避免发送后立即刷新导致读到旧数据
        try {
            const text = new TextDecoder('utf-8').decode(new Uint8Array(payload))
            const body = JSON.parse(text)
            const layer = Number(body?.layer)
            fallbackLayer = Number.isInteger(layer) && layer >= 0 && layer <= 3 ? layer : 0
            const serial = Number(body?.serial)
            const typeRaw = String(body?.type || '').toLowerCase()
            const value = String(body?.value ?? '')
            const usageIds = Array.isArray(body?.usageIds) ? body.usageIds : []

            if (Number.isInteger(layer) && layer >= 0 && layer <= 3 && Number.isInteger(serial) && serial > 0) {
                const normalizedType = ['url', 'text', 'file'].includes(typeRaw) ? typeRaw : 'url'
                const uidList = [...new Set(
                    usageIds
                        .map((item) => Number(item))
                        .filter((uid) => Number.isInteger(uid) && uid >= 0 && uid <= 0xff)
                )]

                let rawStorage = {}
                try {
                    rawStorage = JSON.parse(localStorage.getItem(QUICK_LAUNCH_STORAGE_KEY) || '{}') || {}
                } catch (error) {
                    rawStorage = {}
                }

                const nextLayerEntries = uidList.map((uid) => ({
                    uid,
                    serial,
                    type: normalizedType,
                    value
                }))
                rawStorage[layer] = nextLayerEntries
                localStorage.setItem(QUICK_LAUNCH_STORAGE_KEY, JSON.stringify(rawStorage))

                const viewStorage = buildLayerQuickLaunchViewData(rawStorage)
                localStorage.setItem(QUICK_LAUNCH_VIEW_STORAGE_KEY, JSON.stringify(viewStorage))
            }
        } catch (error) {
            console.warn('[QLSET] 本地快捷启动缓存乐观同步失败:', error)
        }

        sendReportTime.value = getNowTime()
    }
    // 发送 QLDEL 删除快捷启动条目指令（无回传）
    async function sendDeleteQuickLaunch(layer, usageId) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const normalizedLayer = Number(layer)
        const normalizedUsageId = Number(usageId)
        if (!Number.isInteger(normalizedLayer) || normalizedLayer < 0 || normalizedLayer > 3) {
            throw new Error('layer 参数无效，需为 0~3')
        }
        if (!Number.isInteger(normalizedUsageId) || normalizedUsageId < 1 || normalizedUsageId > 0xff) {
            throw new Error('usageId 参数无效，需为 1~255')
        }

        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x51, 0x4c, 0x44, 0x45, 0x4c]
        const header = createDataHeader(headerIdentifier, 0)
        header[10] = normalizedLayer
        header[11] = normalizedUsageId

        await deviceAdapter.sendReport(reportId, header)
        console.log('发送删除快捷启动条目', { layer: normalizedLayer, usageId: normalizedUsageId, header })

        // 乐观更新本地快捷启动缓存，避免删除后立即刷新仍回显旧数据
        try {
            let rawStorage = {}
            try {
                rawStorage = JSON.parse(localStorage.getItem(QUICK_LAUNCH_STORAGE_KEY) || '{}') || {}
            } catch (error) {
                rawStorage = {}
            }

            const layerEntries = Array.isArray(rawStorage[normalizedLayer]) ? rawStorage[normalizedLayer] : []
            rawStorage[normalizedLayer] = layerEntries.filter((entry) => {
                const uid = Number(entry?.uid ?? entry?.keycode)
                return uid !== normalizedUsageId
            })
            localStorage.setItem(QUICK_LAUNCH_STORAGE_KEY, JSON.stringify(rawStorage))

            const viewStorage = buildLayerQuickLaunchViewData(rawStorage)
            localStorage.setItem(QUICK_LAUNCH_VIEW_STORAGE_KEY, JSON.stringify(viewStorage))
        } catch (error) {
            console.warn('[QLDEL] 本地快捷启动缓存乐观同步失败:', error)
        }

        sendReportTime.value = getNowTime()
    }
    // 发送获取整层快捷启动数据
    // 发送 QLDMP 请求：按层获取快捷启动整层数据
    async function sendLayerQuickLaunch(layer) {
        return await sendLayerQuickLaunchByModule(layer)
    }
    // 设置按键到层级上（LYSET）
    async function sendLayerSet(layer = 0, usageId, action = 0, params = []) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const normalizedLayer = Number(layer)
        const normalizedUsageId = Number(usageId)
        const normalizedAction = Number(action)

        if (!Number.isInteger(normalizedLayer) || normalizedLayer < 0 || normalizedLayer > 3) {
            throw new Error('layer 参数无效，需为 0~3')
        }
        if (!Number.isInteger(normalizedUsageId) || normalizedUsageId < 0 || normalizedUsageId > 255) {
            throw new Error('usageId 参数无效，需为 0~255')
        }
        if (!Number.isInteger(normalizedAction) || normalizedAction < 0 || normalizedAction > 255) {
            throw new Error('action 参数无效，需为 0~255')
        }

        const p0 = Number(params?.[0] ?? 0)
        const p1 = Number(params?.[1] ?? 0)
        const p2 = Number(params?.[2] ?? 0)
        const safeParam = (v) => Number.isInteger(v) && v >= 0 && v <= 255 ? v : 0

        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x59, 0x53, 0x45, 0x54]
        const header = createDataHeader(headerIdentifier, 0)
        header[10] = normalizedLayer
        header[11] = normalizedUsageId
        header[12] = normalizedAction
        header[13] = safeParam(p0)
        header[14] = safeParam(p1)
        header[15] = safeParam(p2)

        await deviceAdapter.sendReport(reportId, header)
        console.log('发送按键层级绑定设置', {
            layer: normalizedLayer,
            usageId: normalizedUsageId,
            action: normalizedAction,
            params: [header[13], header[14], header[15]]
        })
        sendReportTime.value = getNowTime()
    }
    // 请求并等待某一层的 QLDMP 完整结果（含超时）
    async function fetchLayerQuickLaunchDump(layer = 0, timeout = 3000) {
        return await fetchLayerQuickLaunchDumpByModule(layer, timeout)
    }

    // 顺序同步 0~3 层快捷启动数据到 localStorage
    async function syncAllLayerQuickLaunchToStorage(timeout = 4000) {
        return await syncAllLayerQuickLaunchToStorageByModule(timeout)
    }
    // 发送获取整层数据
    async function sendLayerData(layer, chunk) {
        return await sendLayerDataByModule(layer, chunk)
    }
    async function requestLayerDump(layer = 0, timeout = 3000) {
        return await requestLayerDumpByModule(layer, timeout)
    }
    // 设置灯光数据
    function setLightData(params) {
        const { type, value } = params
        lightData.value[type] = value
    }
    // 发送切换分页数据
    function sendSwitchPage(params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        // 先发送命令给键盘，不直接修改本地状态
        lightData.value.scr = params
        const fnPage = Number(params) + 1
        if (fnPage >= 1 && fnPage <= 3) {
            keyboardStore.setFnPage(fnPage)
            // console.log(`[Device Store] 同步 Fn 页面: scr=${jsonData.scr} → page=${fnPage}`)
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x41, 0x59, 0x45, 0x52, params];
        const header = createDataHeader(headerIdentifier, 0);
        deviceAdapter.sendReport(reportId, header);
        console.log('发送切换分页命令', params, '等待设备确认...');
        sendReportTime.value = getNowTime()
        // 本地状态 lightData.value.scr 会在设备返回数据时自动更新（见 oninputreport 回调）
        // 这样确保 UI 显示的是设备的真实状态，遵循"设备数据驱动"原则
    }
    // 发送切换皮肤数据
    function sendSwitchSkin(params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x53, 0x54, 0x59, 0x4c, 0x00, params]
        const header = createDataHeader(headerIdentifier, 0);
        deviceAdapter.sendReport(reportId, header)
        console.log('发送切换皮肤命令', params)
        sendReportTime.value = getNowTime()
    }

    // 发送 JSON 数据到上位机（WebSocket 模式）- 直接保存文件
    function sendSaveJson(jsonData, filename) {
        // 检查是否为 WebSocket 模式
        if (deviceAdapter.getMode() !== 'websocket') {
            console.warn('[device.js] sendSaveJson 仅在 WebSocket 模式下可用')
            return
        }

        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        try {
            const message = {
                "event": "webSaveJson",
                "jsonString": JSON.stringify(jsonData, null, 2),
                "name": filename
            }
            // 通过 WebSocket 设备实例发送消息
            deviceAdapter.device.ws.send(JSON.stringify(message))
            console.log('[device.js] 发送 JSON 数据到上位机:', message)
            sendReportTime.value = getNowTime()
        } catch (error) {
            console.error('[device.js] 发送 JSON 数据失败:', error)
            throw error
        }
    }
    // 重置图片数据
    async function sendResetImg(params) {
        if (!deviceAdapter.isOpened) {

            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x49, 0x4d, 0x47, 0x00, ...params]
        const header = createDataHeader(headerIdentifier, 0);
        await deviceAdapter.sendReport(reportId, header)
        console.log('发送重置图片数据')
    }
    // 重置单个按键宏
    async function sendResetKey(params) {
        if (!deviceAdapter.isOpened) {

            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x52, 0x45, 0x4b, 0x45, 0x59, params]
        const header = createDataHeader(headerIdentifier, 0);
        await deviceAdapter.sendReport(reportId, header)
        console.log('发送重置单个按键数据')
    }
    // 发送图片给键盘设备
    async function sendImgToDevice(type, params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        if (type === 'meta') {
            const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x4f, 0x47, ...params]
            const header = createDataHeader(headerIdentifier, 0);
            await deviceAdapter.sendReport(reportId, header)
        } else {
            await deviceAdapter.sendReport(reportId, params)
        }
        console.log('发送图片命令', params)
        sendReportTime.value = getNowTime()
    }
    // 发送休眠时间
    async function sendSleepTime(type, params) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x53, 0x4c, 0x50, 0x54, 0x00, ...params]
        const header = createDataHeader(headerIdentifier, 0);
        await deviceAdapter.sendReport(reportId, header)
        console.log('发送休眠时间', params)
        sendReportTime.value = getNowTime()
    }
    // 发送文件下载 URL 到上位机（WebSocket 模式）
    function sendDownloadUrl(url, filename, useDialog = false) {
        // 检查是否为 WebSocket 模式
        if (deviceAdapter.getMode() !== 'websocket') {
            console.warn('[device.js] sendDownloadUrl 仅在 WebSocket 模式下可用')
            return
        }

        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        try {
            const message = {
                "event": "downUrlFile",
                "url": url,
                "name": filename,
                "useDialog": useDialog
            }
            // 通过 WebSocket 设备实例发送消息
            deviceAdapter.device.ws.send(JSON.stringify(message))
            console.log('[device.js] 发送文件下载 URL 到上位机:', message)
            sendReportTime.value = getNowTime()
        } catch (error) {
            console.error('[device.js] 发送文件 URL 失败:', error)
            throw error
        }
    }
    async function connectDevice() {
        try {
            if (!deviceAdapter.isOpened) {
                throw new Error('没有可用的设备')
            }
            // 监听键盘按下和弹起事件
            window.addEventListener('keydown', handleKeyDown)
            window.addEventListener('keyup', handleKeyUp)

            // 开始录制键盘行为
            recordingActions.value = []
            isRecording.value = true

            // console.log('设备已连接并开始监听键盘事件')
            // console.log('开始录制键盘行为...')
        } catch (error) {
            console.error('连接设备失败:', error)
            throw error
        }
    }

    function handleKeyDown(event) {
        // 检查是否是重复触发（按键已经在按下状态）
        if (pressedKeys.value.has(event.code)) {
            return
        }

        // 添加到已按下的键集合
        pressedKeys.value.add(event.code)

        // 录制按键动作
        recordKeyAction(event, true)
    }

    function handleKeyUp(event) {
        console.log(event)
        // 从已按下的键集合中移除
        pressedKeys.value.delete(event.code)

        // 录制按键动作
        recordKeyAction(event, false)
    }

    // 记录按键动作到录制序列中
    function recordKeyAction(event, isKeyDown) {
        const currentTime = Date.now();

        // 根据时间间隔类型计算时间间隔
        let timeInterval;
        if (timeIntervalType.value === 'default') {
            timeInterval = Number(defaultTimeInterval.value)
        } else {
            timeInterval = recordingActions.value.length > 0 ?
                currentTime - recordingActions.value[recordingActions.value.length - 1].timestamp : 0;
        }

        const action = {
            type: 'key',
            key: event.key,
            keyCode: keycodeObj[event.code],
            code: event.code,
            isKeyDown: isKeyDown,
            timestamp: currentTime,
            timeInterval: timeInterval
        }

        // 将动作添加到临时记录
        recordingActions.value.push(action)
        console.log(`录制动作: ${isKeyDown ? '按下' : '弹起'} ${event.key}`, action)

        // 实时追加到当前宏
        const currentMacro = macroStore.getCurrentMacro();
        if (!currentMacro) {
            console.log('没有选中的宏，动作将只记录在临时列表中');
            return;
        }

        // 有选中的宏，进行实时同步
        console.log('同步动作到宏:', currentMacro.name);
        if (currentMacro) {
            // 获取当前宏的所有动作
            const data = {
                key: action.key,
                keyCode: action.keyCode,
                isKeyDown: action.isKeyDown,
                timeInterval: action.timeInterval
            }

            // 根据模式决定如何添加动作
            if (isInsertMode.value && insertPosition.value >= 0) {
                // 插入模式：在指定位置插入
                let targetIndex = insertPosition.value
                if (insertDirection.value === 'below') {
                    targetIndex = insertPosition.value + 1
                }

                console.log(`插入模式：在位置 ${targetIndex} 插入动作`)
                macroStore.insertActions(currentMacro.id, [data], targetIndex)
                insertPosition.value = targetIndex
            } else {
                // 普通模式：追加到末尾
                macroStore.appendActions(currentMacro.id, [data])
            }

            // 发送更新后的宏数据到键盘
            setTimeout(() => {
                const updatedMacro = macroStore.getCurrentMacro();
                if (updatedMacro) {
                    console.log('发送更新后的宏数据，动作数量:', updatedMacro.actions.length);
                } else {
                    console.error('获取更新后的宏数据失败');
                }
            }, 10)
        }
    }

    // 开始录制宏
    function startRecording() {
        recordingActions.value = []
        isRecording.value = true
        console.log('开始录制键盘宏...')
    }

    // 停止录制宏
    function stopRecording() {
        isRecording.value = false
        console.log('停止录制，共录制了', recordingActions.value.length, '个动作')
        return [...recordingActions.value]
    }

    // 将录制的动作转换为宏动作格式
    function convertToMacroActions(actions = recordingActions.value) {
        if (actions.length === 0) return []

        const result = []

        // 为每个动作计算与上一个动作的时间间隔
        for (let i = 0; i < actions.length; i++) {
            const currentAction = actions[i]
            const previousAction = actions[i - 1]

            // 计算与上一个动作的时间间隔
            const timeInterval = previousAction ?
                currentAction.timestamp - previousAction.timestamp : 0

            result.push({
                ...currentAction,
                timeInterval: timeInterval
            })
        }

        return result
    }
    // 清空当前录制
    function clearRecording() {
        recordingActions.value = []
        isRecording.value = false
        console.log('已清空录制数据')
    }

    // 关闭设备连接
    async function closeDevice() {
        if (device.value) {
            await device.value.close()
            // isConnected.value = false
            device.value = null
            isOpened.value = false
            console.log('设备已关闭')
        }
    }

    async function trySwitchAuthorizedDevice(targetLinkMode) {
        if (deviceAdapter.getMode() !== 'webhid') {
            return { success: false, reason: 'not_webhid' }
        }
        if (!targetLinkMode || (targetLinkMode !== 'wired' && targetLinkMode !== 'wireless')) {
            return { success: false, reason: 'invalid_mode' }
        }

        const currentVid = Number(deviceVendorId.value)
        const currentPid = Number(deviceProductId.value)
        const pairedConfig = getPairedDeviceConfig(currentVid, currentPid, targetLinkMode)
        if (!pairedConfig) {
            needReauthorize.value = true
            return { success: false, reason: 'no_paired_config' }
        }

        const authorizedDevices = await navigator.hid.getDevices()
        const candidateDevices = authorizedDevices.filter(dev =>
            dev.vendorId === pairedConfig.vendorId && dev.productId === pairedConfig.productId
        )
        const index = findOuterIndexByReportId(candidateDevices, 4)
        if (index === -1) {
            needReauthorize.value = true
            return { success: false, reason: 'not_authorized' }
        }

        const targetDevice = candidateDevices[index]
        try {
            await closeDevice()
            const switched = await setDevice(targetDevice)
            if (switched) {
                needReauthorize.value = false
                return { success: true }
            }
            needReauthorize.value = true
            return { success: false, reason: 'set_device_failed' }
        } catch (error) {
            console.error('[device.js] 自动切换设备失败:', error)
            needReauthorize.value = true
            return { success: false, reason: 'switch_exception', error }
        }
    }

    async function handleReportedLinkMode(nextLinkMode) {
        if (deviceAdapter.getMode() !== 'webhid') {
            return { success: true, skipped: true }
        }
        if (nextLinkMode !== 'wired' && nextLinkMode !== 'wireless') {
            return { success: false, reason: 'invalid_mode' }
        }
        if (currentLinkMode.value === nextLinkMode && isOpened.value) {
            return { success: true, skipped: true }
        }
        return await trySwitchAuthorizedDevice(nextLinkMode)
    }

    function normalizeLinkMode(mode) {
        const text = String(mode ?? '').toLowerCase().trim()
        if (!text) return null
        if (text === 'wired' || text === 'usb' || text === 'cable' || text === '0') return 'wired'
        if (text === 'wireless' || text === 'rf' || text === '2.4g' || text === '250byte' || text === '1') return 'wireless'
        return null
    }

    function setReportedLinkMode(mode) {
        const normalized = normalizeLinkMode(mode)
        if (!normalized) return
        reportedLinkMode.value = normalized
    }

    function disconnectDevice() {
        // 移除键盘事件监听器
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)

        // 如果正在录制，停止录制
        if (isRecording.value) {
            console.log('停止录制...')
            isRecording.value = false

            const recordedCount = recordingActions.value.length
            if (recordedCount > 0) {
                console.log('本次录制动作数量:', recordedCount)
                console.log('录制的动作已实时同步到键盘')
            } else {
                console.log('本次录制没有动作')
            }

            // 清空临时记录
            recordingActions.value = []
        } else if (isRecording.value) {
            console.log('录制模式已停止，但没有录制到任何动作')
            isRecording.value = false
            recordingActions.value = []
        }

        // 清空按键状态
        pressedKeys.value.clear()
    }

    // 注册网页需要监听的命令（仅 WebSocket 模式）
    async function registerCommand(commandHeader) {
        try {
            await deviceAdapter.registerCommand(commandHeader)
        } catch (error) {
            console.error('[device store] 注册命令失败:', error)
            throw error
        }
    }

    // 重置图片ACK状态（开始一次新的图片验包流程前调用）
    function resetImageAckState() {
        isWaitingImageAck.value = true
        imageAckReceived.value = false
        imageAckMissingPackets.value = []
    }

    // 读取并消费一次图片ACK结果
    // 返回 null: 尚未收到ACK
    // 返回 []: 收到ACK且无丢包（第10位即0xff）
    // 返回 [n...]: 收到ACK且有丢包序号（直到0xff结束）
    function consumeImageAckResult() {
        if (!imageAckReceived.value) {
            return null
        }
        isWaitingImageAck.value = false
        imageAckReceived.value = false
        return [...imageAckMissingPackets.value]
    }

    // 解析图片ACK：从索引10开始读取，0xff表示列表结束
    function handleImagePacketAck(ackData) {
        if (!isWaitingImageAck.value) {
            return
        }
        const missingPackets = []
        for (let i = 10; i < ackData.length; i++) {
            const byte = ackData[i]
            if (byte === 0xff) {
                break
            }
            if (byte !== 0x00) {
                missingPackets.push(byte)
            }
        }

        imageAckMissingPackets.value = missingPackets
        imageAckReceived.value = true

        if (missingPackets.length > 0) {
            console.log('[图片ACK] 检测到丢包:', missingPackets)
        } else {
            console.log('[图片ACK] 无丢包，传输完整')
        }
    }

    // 处理设备ACK响应 - 检测丢失的包并重传
    function handlePacketAck(ackData) {
        // 清除超时计时器
        if (ackTimeout.value) {
            clearTimeout(ackTimeout.value)
            ackTimeout.value = null
        }

        ackReceived.value = true

        // 解析ACK数据：从索引6开始是丢失包的标记
        // 0xff 表示所有包都完整，没有丢包
        // 其他值表示丢失的包序号
        const lostPacketData = ackData.slice(6)
        const missingPackets = []

        // 检查哪些包丢失了
        for (let i = 0; i < lostPacketData.length; i++) {
            const byte = lostPacketData[i]
            if (byte === 0xff) {
                // 0xff 表示所有包都完整
                console.log('[ACK] 所有数据包完整，传输成功！')
                missingPackets.length = 0
                break
            } else if (byte !== 0x00) {
                // 非0非0xff的值表示丢失的包序号
                missingPackets.push(byte)
            }
        }

        if (missingPackets.length > 0) {
            console.log('[ACK] 检测到丢失的包:', missingPackets)
            lostPackets.value = missingPackets
            // 触发重传丢失的包
            retransmitLostPackets()
        } else {
            console.log('[ACK] 所有包都已成功接收')
            lostPackets.value = []
        }
    }

    // 重传丢失的包
    async function retransmitLostPackets() {
        if (lostPackets.value.length === 0) {
            console.log('[重传] 没有需要重传的包')
            return
        }

        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))
        const reportId = 0x04

        console.log(`[重传] 开始重传 ${lostPackets.value.length} 个丢失的包`)

        try {
            for (const packetNum of lostPackets.value) {
                // packetNum 是1-based的包序号，但在currentPackets数组中是0-based索引
                const packetIndex = packetNum - 1

                if (packetIndex >= 0 && packetIndex < currentPackets.value.length) {
                    const packet = currentPackets.value[packetIndex]

                    // 构造包含序号的数据包
                    const retransmitPacket = new Uint8Array(packet.length + 1)
                    retransmitPacket[0] = packetNum // 包序号
                    retransmitPacket.set(packet, 1) // 原始数据

                    console.log(`[重传] 重新发送第 ${packetNum} 个包`)
                    await deviceAdapter.sendReport(reportId, retransmitPacket)

                    // 包之间等待延时
                    await delay(50)
                } else {
                    console.warn(`[重传] 包序号 ${packetNum} 超出范围，无法重传`)
                }
            }

            console.log('[重传] 所有丢失的包已重新发送，发送结束命令...')

            // ✨ 重传所有包后，也需要发送结束命令来完成传输
            // 这确保设备知道重传已完成，可以进行数据验证
            const endCommand = new Uint8Array([
                0x43, 0x52, 0x54, 0x00, 0x00, 0x50, 0x49, 0x58,
                0x4d, 0x41, 0x50, 0x45, 0x4e, 0x44, 0x52
            ]);
            try {
                await deviceAdapter.sendReport(reportId, endCommand)
                console.log('[重传] 结束命令发送成功，等待设备ACK响应...')
            } catch (error) {
                console.error('[重传] 结束命令发送失败:', error)
                throw error
            }

            // 清空丢失包列表，重新等待ACK
            lostPackets.value = []
            ackReceived.value = false

            // 重新设置ACK超时计时器
            setupAckTimeout()
        } catch (error) {
            console.error('[重传] 重传包过程中出错:', error)
            throw error
        }
    }

    // 设置ACK超时计时器
    function setupAckTimeout() {
        if (ackTimeout.value) {
            clearTimeout(ackTimeout.value)
        }

        ackTimeout.value = setTimeout(() => {
            console.error('[ACK] 等待ACK超时，设备可能未正确接收数据')
            ackReceived.value = false
            ackTimeout.value = null
            lostPackets.value = []
        }, ackWaitTime.value)
    }

    // 设置 WebHID 传输模式
    function setTransmitMode(mode) {
        if (mode === 'wired' || mode === '250byte') {
            transmitMode.value = mode
            console.log(`[device.js] 传输模式已设置为: ${mode}`)
        } else {
            console.warn(`[device.js] 无效的传输模式: ${mode}，使用默认 'wired'`)
            transmitMode.value = 'wired'
        }
    }

    // 批量注册多个命令（仅 WebSocket 模式）
    async function registerCommands(commands) {
        try {
            await deviceAdapter.registerCommands(commands)
        } catch (error) {
            console.error('[device store] 批量注册命令失败:', error)
            throw error
        }
    }

    return {
        device,
        productName,
        deviceVendorId,
        deviceProductId,
        // isConnected,
        pressedKeys,
        // 录制相关状态
        isRecording,
        recordingActions,
        sendReportTime,
        dataBuffer,
        lightData,
        rgbFrameEnabled,
        rgbFrameSeq,
        rgbFrameFps,
        rgbFrameBuffer,
        currentLinkMode,
        reportedLinkMode,
        needReauthorize,
        isOpened,
        // 基础功能
        setDevice,
        connectDevice,
        disconnectDevice,
        // 宏录制功能
        startRecording,
        stopRecording,
        clearRecording,
        convertToMacroActions,
        // 宏 store
        macroStore,
        // 发送数据到键盘
        sendReport,
        sendLightEffect,
        sendResetEffect,
        sendGetMacroByDevice,
        sendKeyReplaceByDevice,
        sendLuminance,
        sendSpeed,
        sendGetLightData,
        sendOs,
        sendColor,
        sendKeyBinding,
        sendSwitchPage,
        sendSwitchSkin,
        sendSaveJson,
        sendDownloadUrl,
        sendHs,
        sendImgToDevice,
        sendResetImg,
        sendResetKey,
        sendGetCrc,
        sendPocketLoss,
        sendImgOver,
        resetImageAckState,
        consumeImageAckResult,
        sendImgPocketLoss,
        stopRgbFrameSync,
        startRgbFrameSync,
        sendSleepTime,
        sendQuickLaunch,
        sendDeleteQuickLaunch,
        sendLayerSet,
        sendLayerData,
        requestLayerDump,
        sendLayerQuickLaunch,
        fetchLayerQuickLaunchDump,
        syncAllLayerQuickLaunchToStorage,
        buildLayerQuickLaunchViewData,
        syncLayerQuickLaunchViewStorage,
        // 时间间隔相关
        timeIntervalType,
        setTimeIntervalType,
        defaultTimeInterval,
        setDefaultTimeInterval,
        setLightData,
        // 插入模式相关
        isInsertMode,
        insertPosition,
        insertDirection,
        setInsertMode,
        // 关闭设备
        closeDevice,
        trySwitchAuthorizedDevice,
        handleReportedLinkMode,
        setReportedLinkMode,
        // 时间间隔类型
        timeIntervalType,
        setTimeIntervalType,
        // WebHID 传输模式
        transmitMode,
        setTransmitMode,
        // WebSocket 命令注册（仅 WebSocket 模式）
        registerCommand,
        registerCommands,
        // 图片接收相关
        handleImageData,
        handleImageEnd,
        getImageFromStorage,
        getImageDataUrl,
        imageReceiveBuffer,
        imageRequestQueue,
        isReceivingImage,
        processImageRequestQueue,
        // Dock 连接状态
        dockConnect,
        layerDumpData
    }
})
